import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Reminder {
  id: string;
  user_id: string;
  workspace_id?: string;
  title: string;
  description?: string;
  due_at?: string;
  status: 'open' | 'done' | 'dismissed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
}

export function useReminders() {
  const queryClient = useQueryClient();

  const { data: reminders = [], ...query } = useQuery({
    queryKey: ['reminders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reminders')
        .select('*')
        .eq('status', 'open')
        .order('due_at', { ascending: true, nullsFirst: false })
        .limit(50);
      
      if (error) throw error;
      return data as Reminder[];
    },
    staleTime: 30_000,
    retry: 0,
  });

  const overdueCount = reminders.filter(r => 
    r.due_at && new Date(r.due_at) < new Date()
  ).length;

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'done' | 'dismissed' }) => {
      const { error } = await supabase
        .from('reminders')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
  });

  const createReminder = useMutation({
    mutationFn: async (reminder: Omit<Reminder, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('reminders')
        .insert([{
          ...reminder,
          user_id: user.user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
  });

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('reminders-realtime')
      .on('postgres_changes', 
        {
          event: '*',
          schema: 'public',
          table: 'reminders',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['reminders'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    data: reminders,
    overdueCount,
    updateStatus: updateStatus.mutate,
    createReminder: createReminder.mutate,
    isUpdating: updateStatus.isPending,
    isCreating: createReminder.isPending,
    ...query,
  };
}